import { Shield, Lock, FileText, Server, Users, Cpu, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { openChat } from "@/lib/chat";
import { track } from "@/lib/analytics";
import { useHashScroll } from "@/hooks/useHashScroll";
import { SidebPill } from "@/components/SidebPill";

const copyShareLink = async (sectionId, title) => {
  const url = `${window.location.origin}/security#${sectionId}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied", { description: `${title} → ${url}` });
    track("security_section_link_copied", { section_id: sectionId });
  } catch {
    toast.error("Couldn't copy — long-press the URL bar to share.");
  }
};

const Section = ({ icon: Icon, title, children, testId, share, highlighted }) => (
  <div
    id={testId}
    className={`card-sharp p-7 scroll-mt-24 transition-shadow duration-500 ${
      highlighted ? "ring-2 ring-[#ac62ea] shadow-[0_0_0_8px_rgba(172,98,234,0.18)]" : ""
    }`}
    data-testid={testId}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg bg-[#ac62ea]/12 flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#ac62ea]" />
      </div>
      <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
      {share && (
        <a
          href={`#${testId}`}
          onClick={() => copyShareLink(testId, title)}
          className="ml-1 text-muted-foreground/60 hover:text-[#ac62ea] transition-colors p-1 rounded"
          title="Open / copy share link"
          aria-label={`Share link for ${title}`}
          data-testid={`${testId}-share`}
        >
          <Link2 className="h-4 w-4" />
        </a>
      )}
    </div>
    <div className="text-sm text-foreground/85 leading-relaxed space-y-3">{children}</div>
  </div>
);

const subprocessors = [
  { name: "Stripe", role: "Payments processor (PCI DSS Level 1 + SOC 2 Type II)", region: "USA / EEA", url: "https://stripe.com/privacy" },
  { name: "MongoDB Atlas", role: "Primary application database (SOC 2 Type II, ISO 27001)", region: "AWS us-east-1", url: "https://www.mongodb.com/legal" },
  { name: "Google Workspace", role: "Email & calendar (rev@sideb.io) — SOC 2 Type II", region: "USA / EEA", url: "https://workspace.google.com/security/" },
  { name: "Emergent Platform", role: "Application hosting & Google OAuth broker (SOC 2)", region: "Multi-region", url: "https://emergent.sh" },
];

export default function Security() {
  const highlighted = useHashScroll();

  return (
    <div className="bg-white" data-testid="security-page">
      <section className="bg-ink text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-24 pb-20">
          <div className="label-mono" style={{ color: "rgba(255,255,255,0.55)" }}>Security &amp; Trust</div>
          <h1 className="font-display text-5xl sm:text-6xl font-semibold mt-5 tracking-[-0.04em] leading-[0.98]">
            How we handle your data.
          </h1>
          <p className="text-lg text-white/65 mt-7 max-w-2xl leading-relaxed">
            <SidebPill variant="dark" /> operates as a boutique studio with enterprise-grade vendor handling. By design, the heavy lifting on certification sits with our subprocessors — each one is independently audited to SOC 2 Type II or higher — and our internal practices map to the same controls. This page is the document your procurement team should ask for.
          </p>
          <div className="flex flex-wrap gap-3 mt-9">
            <div className="px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/15 text-xs text-white/85 font-medium">TLS 1.2+ everywhere</div>
            <div className="px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/15 text-xs text-white/85 font-medium">Data at rest encrypted (AES-256)</div>
            <div className="px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/15 text-xs text-white/85 font-medium">All subprocessors SOC 2 Type II</div>
            <div className="px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/15 text-xs text-white/85 font-medium">Enterprise LLMs · zero-training</div>
            <div className="px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/15 text-xs text-white/85 font-medium">Mutual NDA ready</div>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <div className="grid md:grid-cols-2 gap-5">
            <Section icon={Lock} title="Encryption" testId="sec-encryption" highlighted={highlighted === "sec-encryption"}>
              <p>All client data is encrypted in transit via TLS 1.2 or higher. All data at rest in MongoDB Atlas is encrypted with AES-256 using AWS-managed KMS keys.</p>
              <p>Payment information never touches our servers — Stripe handles card data directly and returns only opaque session identifiers. We are not in the PCI scope.</p>
            </Section>

            <Section icon={Users} title="Access controls" testId="sec-access" highlighted={highlighted === "sec-access"}>
              <p>SideB is a tightly-scoped studio. Engagement leads have access to client data only for engagements they&apos;re assigned to; no shared logins, no offshore subcontractors.</p>
              <p>All vendor consoles (Stripe, MongoDB Atlas, Google Workspace) are MFA-enforced. Session tokens for client dashboards are HttpOnly, Secure, SameSite=None cookies with 7-day expiry.</p>
            </Section>

            <Section icon={Server} title="Hosting &amp; subprocessors" testId="sec-hosting" highlighted={highlighted === "sec-hosting"}>
              <p>The app is hosted on the Emergent platform (SOC 2). The primary database is MongoDB Atlas. Authentication is brokered by Google OAuth. Payments by Stripe.</p>
              <p className="font-mono text-xs text-muted-foreground">Updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
            </Section>

            <Section icon={FileText} title="NDAs &amp; contracts" testId="sec-contracts" share highlighted={highlighted === "sec-contracts"}>
              <p>A mutual NDA is signed before any engagement begins. We&apos;re happy to sign yours, or we can send ours — both paths land at the same outcome.</p>
              <p>Send your NDA or request ours via <a href="mailto:rev@sideb.io" className="text-[#ac62ea] hover:underline">rev@sideb.io</a>.</p>
            </Section>

            <Section icon={Cpu} title="AI tooling &amp; data handling" testId="sec-ai-tooling" highlighted={highlighted === "sec-ai-tooling"}>
              <p>All AI tooling we use for synthesis, modeling, and diagnostics runs on <b>enterprise tiers with contractual zero-training</b>: client prompts and completions are never used to train third-party models, and never retained beyond the active session window.</p>
            </Section>

            <Section icon={Shield} title="Data retention &amp; deletion" testId="sec-retention" highlighted={highlighted === "sec-retention"}>
              <p>Engagement records (bookings, intake forms, quotes) are retained for 24 months by default for tax and accounting purposes, then purged.</p>
              <p>Clients may request full deletion at engagement end via <a href="mailto:rev@sideb.io" className="text-[#ac62ea] hover:underline">rev@sideb.io</a>; we honor within 30 days where not blocked by legal hold.</p>
            </Section>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-ice-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <div className="label-mono">Subprocessors</div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-3 tracking-[-0.03em] leading-tight">Who touches your data.</h2>
          <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">
            We use a small, deliberate stack. Every subprocessor below is itself audited to SOC 2 Type II or higher.
          </p>
          <div className="mt-8 card-sharp overflow-hidden">
            <table className="w-full text-sm" data-testid="subprocessors-table">
              <thead className="bg-white border-b border-border text-left">
                <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="p-4">Vendor</th>
                  <th className="p-4">Purpose &amp; certification</th>
                  <th className="p-4">Data region</th>
                </tr>
              </thead>
              <tbody>
                {subprocessors.map((s) => (
                  <tr key={s.name} className="border-b border-border last:border-0 align-top bg-white" data-testid={`subprocessor-${s.name.replace(/\s+/g, "-").toLowerCase()}`}>
                    <td className="p-4 font-semibold">
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#ac62ea]">{s.name} ↗</a>
                    </td>
                    <td className="p-4 text-muted-foreground">{s.role}</td>
                    <td className="p-4 text-muted-foreground">{s.region}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 text-center">
          <h3 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.03em]">Let&apos;s get an NDA in place.</h3>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
            Send us your NDA and we&apos;ll counter-sign within one business day — or request ours and we&apos;ll have it back to you the same day.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-6" data-testid="security-send-nda-cta">
              <a
                href="mailto:rev@sideb.io?subject=Sending%20our%20NDA%20for%20countersignature&body=Hi%20SideB%20team%2C%0A%0APlease%20find%20our%20NDA%20attached%20for%20your%20review%20and%20countersignature.%0A%0AThanks%2C%0A"
                onClick={() => openChat({ context: "Security: Sending NDA for countersignature" })}
              >Send your NDA</a>
            </Button>
            <Button asChild variant="outline" className="btn-pill h-11 px-6" data-testid="security-request-nda-cta">
              <a
                href="mailto:rev@sideb.io?subject=Requesting%20SideB%27s%20mutual%20NDA&body=Hi%20SideB%20team%2C%0A%0ACould%20you%20send%20over%20your%20mutual%20NDA%20for%20us%20to%20review%20and%20sign%3F%0A%0AThanks%2C%0A"
                onClick={() => openChat({ context: "Security: Requesting SideB's mutual NDA" })}
              >Request our NDA</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
